/**
 * Deployment configuration for the OAuth consent surface (#2443 slice 4).
 *
 * Nothing here names a vendor, a client, a host or a deployment.
 *
 * The two paths are the Console's OWN addresses, fixed like every other route
 * (router.tsx declares them as literals, which is what the route census reads;
 * config.test.ts pins that they agree). GoTrue is pointed at the consent page
 * by GOTRUE_OAUTH_SERVER_AUTHORIZATION_PATH, whose dist default is this path —
 * GoTrue follows the Console, not the other way round. The resource shown to
 * the person is the deployment's own MCP_INTERNAL_RESOURCE, passed in at build
 * like every Console setting (VITE_*).
 */

import { runtimeEnv } from '@solum/auth/runtime-config'
/** Where GoTrue sends the browser to ask for consent (GOTRUE_OAUTH_SERVER_AUTHORIZATION_PATH). */
export const CONSENT_PATH = '/oauth/consent'

/** The personal Connected apps destination (account menu). */
export const CONNECTED_APPS_PATH = '/account/connected-apps'

/** A configured protected-resource URI, exactly as configured — or null. It is
 *  shown to the person verbatim, so it must be an absolute https URI that is a
 *  plain public identifier: no credentials, no fragment (RFC 8707 §2). Anything
 *  else is not shown at all; the page then names "this Console's tools". */
export function resourceFrom(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null
  const value = raw.trim()
  let u: URL
  try { u = new URL(value) } catch { return null }
  return u.protocol === 'https:' && !u.username && !u.password && !u.hash && !value.includes('#') ? value : null
}

/** The protected resource a remote client is asking to use: the deployment's
 *  MCP_INTERNAL_RESOURCE, which scripts/build-web-env.mjs passes to the console
 *  build as VITE_MCP_RESOURCE. Null when the deployment did not provide it. */
export const MCP_RESOURCE: string | null = resourceFrom(runtimeEnv('VITE_MCP_RESOURCE'))

/** The OAuth issuer GoTrue signs with (#2512 RFC 9207): an exact https URL with
 *  no query, fragment, credentials or trailing slash — or null. scripts/
 *  build-web-env.mjs derives it exactly as the relay's MCP_OAUTH_ISSUER is
 *  derived (GOTRUE_JWT_ISSUER, else SUPABASE_URL + /auth/v1), so the consent
 *  page and the relay's front always echo the same issuer. */
export function issuerFrom(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const value = raw.trim()
  let u: URL
  try { u = new URL(value) } catch { return null }
  const exact = u.protocol === 'https:' && !u.username && !u.password && !u.search && !u.hash
    && !value.endsWith('/') && !value.includes('#') && !value.includes('?')
  return exact ? value : null
}

export const OAUTH_ISSUER: string | null = issuerFrom(runtimeEnv('VITE_OAUTH_ISSUER'))
